import React, {useEffect, useState} from 'react';
import {usePagination} from "../../hooks/usePagination";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {ITableColumn, TableWithPagingAndFilter} from "../../components/table";
import {
    EntityWithIdAndDescription,
    EntityWithIdAndDescriptionFields,
    EntityWithIdFields,
    QueryStatus
} from "../../types";
import Grid from "@mui/material/Grid";
import {AsyncSelect, ControlledDatePicker, ControlledTextField} from "../../components/forms";
import Button from "@mui/material/Button";
import SearchIcon from '@mui/icons-material/Search';
import IconButton from "@mui/material/IconButton";
import {ListItemIcon, MenuItem, Stack, TextField, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import Icon from "../../@core/components/icon";

import {HttpDumpTrucks} from "./api/HttpDumpTrucks";
import {
    DumpTruckFilterDTO,
    DumpTruckStatusOption,
    DumpTruckSummarySummary,
    DumpTruckViewDTO,
    schemaDumpTruckFilter
} from "./types/dto";
import {DumpTruckFields, DumpTruckFilterFields} from "./types/fields";
import {getDumpTruckStatusColor} from "./types/typification";
import {ControlledAutocomplete} from "../../components/forms/ControlledAutocomplete";
import {ControlledDateRangePicker} from "../../components/forms/ControlledDateRangePicker";
import {OrderFields} from "../orders/types/fields";
import DumpTruckStatusChip from "./components/DumpTruckStatusChip";
import toast from "react-hot-toast";
import DumpTruckStatusChipWithMenu from "./components/DumpTruckStatusChipWithMenu";
import {useConfirmationDialog} from "../../hooks/useConfirmationDialog";
import DumpTruckCreateDialog from "./components/DumpTruckCreateDialog";
import DumpTruckSummaryCard from "./components/DumpTruckSummaryCard";
import {dateFormatter} from "../../utils/formatters";
import DatePicker from "react-datepicker";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import esLocale from "date-fns/locale/es";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {MobileDatePicker} from "@mui/x-date-pickers";
import {DumpTruckStatusTypes} from "./types/enum";
import {differenceInCalendarDays} from "date-fns";


function DumpTrucks() {
    const {askConfirmation} = useConfirmationDialog()

    const [showFilter, setShowFilter] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogType, setDialogType] = useState<'view' | 'create'>('view');
    const [selectedDumpTruck, setSelectedDumpTruck] = useState<DumpTruckViewDTO>();

    const [statusOptionList, setStatusOptionList] = useState<DumpTruckStatusOption[]>([]);
    const [typeOptionList, setTypeOptionList] = useState<EntityWithIdAndDescription[]>([]);

    const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
    const [summary, setSummary] = useState<DumpTruckSummarySummary>();

    const onSearch = (data: DumpTruckFilterDTO) => {
        setFilterAndSearch(data);
    };

    const {control, getValues, handleSubmit} = useForm<DumpTruckFilterDTO>({
        defaultValues: {
            [DumpTruckFilterFields.IsBilled]: null,
            [DumpTruckFilterFields.Client]: '',
            [DumpTruckFilterFields.Address]: '',
            [DumpTruckFilterFields.StatusCodeList]: [DumpTruckStatusTypes.ACTIVE],
            [DumpTruckFilterFields.TypeCodeList]: [],
        },
        resolver: zodResolver(schemaDumpTruckFilter)
    });

    const {
        entities: dumpTrucks,
        status: dumpTrucksStatus,
        paginacion: pagination,
        onChange,
        refetch,
        setFilterAndSearch
    } = usePagination<DumpTruckViewDTO, DumpTruckFilterDTO>({
        initialFilter: {...getValues()},
        loader: (pagination, filter) => HttpDumpTrucks.get(pagination, filter)
    });


    useEffect(() => {
        HttpDumpTrucks.getSummary().then(setSummary).finally(() => setLoadingSummary(false));
    }, [dumpTrucks]);

    const columns: ITableColumn[] = [
        {
            label: "Cliente / Dirección",
            onRenderCell: (row: DumpTruckViewDTO) => (
                <Stack>
                    <Typography variant='body1' fontWeight={600}>{row[DumpTruckFields.Client]}</Typography>
                    <Typography variant='caption'>{row[DumpTruckFields.Address]}</Typography>
                </Stack>
            ),
            width: '30%',

        },
        {
            label: "Tipo",
            value: DumpTruckFields.TypeDescription,
            width: '5%',
        },
        {
            label: "Fechas",
            onRenderCell: (row: DumpTruckViewDTO) => (
                <Stack>
                    <Typography variant='body2' sx={{fontSize: '0.75rem'}}>
                        <b>Entrega:</b> {row[DumpTruckFields.DeliveryDate] ? dateFormatter.toShortDate(row[DumpTruckFields.DeliveryDate]) : '-'}
                    </Typography>
                    <Typography variant='body2' sx={{fontSize: '0.75rem'}}>
                        <b>Retiro:</b> {row[DumpTruckFields.RemovalDate] ? dateFormatter.toShortDate(row[DumpTruckFields.RemovalDate]) : '-'}
                    </Typography>
                </Stack>
            ),
            width: '20%',
        },
        {
            label: "Días",
            onRenderCell: (row: DumpTruckViewDTO) => {
                let days = null;
                if (row[DumpTruckFields.DeliveryDate] && row[DumpTruckFields.RemovalDate]) {
                        const delivery = new Date(row[DumpTruckFields.DeliveryDate]);
                        const removal = new Date(row[DumpTruckFields.RemovalDate]);
                        const diff = differenceInCalendarDays(removal, delivery) + 1;

                        days = (diff >= 0 ? diff : 0);


                }
                const daysStr = days ? days : '-';

                return(
                <Typography sx={{fontSize: '0.75rem'}}>
                    {daysStr}
                </Typography>
            )},
            width: '5%',
        },
        {
            label: "Remito / Factura",
            width: '15%',
            onRenderCell: (row: DumpTruckViewDTO) => (
                <Stack>
                    <Typography variant='body2'>
                        {row[DumpTruckFields.DeliveryNote] || 'Sin Remito'}
                    </Typography>
                    <Typography variant='body2'>
                        {row[DumpTruckFields.Invoice] || 'Sin Factura'}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: "Importe",
            width: '5%',
            onRenderCell: (row: DumpTruckViewDTO) => (
                <Stack>

                    <Typography variant='body2' color={'primary'}>
                        <b>{row[DumpTruckFields.Amount]?.toLocaleString('es-AR', {
                            style: 'currency',
                            currency: 'ARS'
                        })}</b>
                    </Typography>
                </Stack>
            ),
        },
        {
            label: "Estado",
            width: '10%',
            onRenderCell: (row: DumpTruckViewDTO) => {
                const statusOption = {
                    [DumpTruckFields.StatusCode]: row[DumpTruckFields.StatusCode],
                    [DumpTruckFields.StatusDescription]: row[DumpTruckFields.StatusDescription]
                };

                return (
                    <DumpTruckStatusChipWithMenu
                        option={row}
                        optionList={statusOptionList}
                        onOptionChange={(newOption) => {
                            const isChangingToDischarged = newOption[DumpTruckFields.StatusCode] === DumpTruckStatusTypes.UNLOADED;

                            let updatedDialogData = {
                                observations: row[DumpTruckFields.Observations] || '',
                                removalDate: isChangingToDischarged
                                    ? new Date()
                                    : (row[DumpTruckFields.RemovalDate] ? new Date(row[DumpTruckFields.RemovalDate]) : null)
                            };

                            const DialogContent = () => {
                                const [observations, setObservations] = useState(updatedDialogData.observations);
                                const [removalDate, setRemovalDate] = useState<Date | null>(updatedDialogData.removalDate);

                                useEffect(() => {
                                    updatedDialogData = { observations, removalDate };
                                }, [observations, removalDate]);

                                return (
                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                                        <Stack direction={'column'} spacing={3} sx={{ mt: 4 }}>
                                            <TextField
                                                label={'Observaciones'}
                                                defaultValue={observations}
                                                onChange={(event) => setObservations(event.target.value)}
                                                rows={4}
                                                multiline
                                                fullWidth
                                            />

                                            {isChangingToDischarged && (
                                                <MobileDatePicker
                                                    label="Fecha de Retiro"
                                                    value={removalDate}
                                                    onChange={(newDate) => setRemovalDate(newDate)}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            size: 'small',
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </LocalizationProvider>
                                );
                            };

                            return askConfirmation(
                                () => {
                                    const promise = HttpDumpTrucks.put(row[EntityWithIdFields.Id], {
                                        ...row,
                                        [DumpTruckFields.StatusCode]: newOption[DumpTruckFields.StatusCode],
                                        [DumpTruckFields.Observations]: updatedDialogData.observations,
                                        [DumpTruckFields.RemovalDate]: updatedDialogData.removalDate,
                                    });

                                    toast.promise(promise, {
                                        success: 'Actualizado',
                                        error: 'No se pudo actualizar',
                                        loading: 'Actualizando'
                                    }).then(() => refetch());
                                },
                                `¿Desea actualizar el estado a: ${newOption[DumpTruckFields.StatusDescription]}?`,
                                <DialogContent />
                            );
                        }}
                    />
                )
            },
        },
        {
            label: "Acciones",
            width: '5%',
            onRenderCell: (row: DumpTruckViewDTO) => {
                return (
                    <Stack direction={'row'}>
                        <Tooltip title='Ver Detalles'>
                            <IconButton
                                sx={{p: 1}}
                                onClick={() => {
                                    setSelectedDumpTruck(row);
                                    setDialogType('view');
                                    setDialogOpen(true);
                                }}>
                                <SearchIcon color='primary'/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
        }
    ];

    useEffect(() => {

        HttpDumpTrucks.getStatusTypes().then(data => {
            setStatusOptionList(data.map(d => ({
                [OrderFields.StatusCode]: d[EntityWithIdAndDescriptionFields.Id],
                [OrderFields.StatusDesc]: d[EntityWithIdAndDescriptionFields.Description]
            })))
        });
        HttpDumpTrucks.getTypes().then(data => setTypeOptionList(data));
    }, []);

    return (
        <>
            {
                !(loadingSummary || summary === undefined) &&
                <DumpTruckSummaryCard summary={summary} />
            }

            <TableWithPagingAndFilter
                title='Administración de Volquetes'
                entityPaginada={dumpTrucks && pagination ? {lista: dumpTrucks, paginacion: pagination} : undefined}
                columns={columns}
                error={dumpTrucksStatus === QueryStatus.Error}
                isLoading={dumpTrucksStatus === QueryStatus.Loading || dumpTrucksStatus === QueryStatus.Idle}
                onShowChildren={() => setShowFilter(!showFilter)}
                showChildren={showFilter}
                childrenName={"Filtros"}
                onPaging={onChange}
                onNewRegister={() => {
                    setSelectedDumpTruck(undefined); // Limpiar selección previa
                    setDialogType('create');
                    setDialogOpen(true);
                }}

                // selectable={true}
                // handleSelectedAction={(selected) => {
                //     const selectedDumpTrucks = dumpTrucks.filter(dt => selected.includes(dt.id));
                //     // generatePDF(selectedDumpTrucks);
                // }}
                // selectedActionName={'Descargar PDF'}
                // selectedActionIcon={<Icon icon={'material-symbols:download'}/>}
            >
                <Grid container spacing={4} sx={{p: 5}}>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control} name={DumpTruckFilterFields.Client} label={'Cliente'}
                                             fullWidth/>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control} name={DumpTruckFilterFields.Address} label={'Dirección'}
                                             fullWidth/>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledAutocomplete
                            loadOptions={HttpDumpTrucks.getTypes}
                            control={control}
                            name={DumpTruckFilterFields.TypeCodeList}
                            label={'Tipo'}
                            valueField={'id'}
                            multiple
                        />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control} name={DumpTruckFilterFields.DeliveryNote}
                                             label={'Nro. Remito'} fullWidth/>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control} name={DumpTruckFilterFields.Invoice}
                                             label={'Nro. Factura'} fullWidth/>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <AsyncSelect
                            name={DumpTruckFilterFields.IsBilled}
                            control={control}
                            label="Facturado"

                            // en el schema se hace trampa para convertir estos valores a true false o null
                            loadOptions={() => Promise.resolve([
                                {id: 1, descripcion: 'Sí'},
                                {id: 2, descripcion: 'No'},
                                {id: -1, descripcion: 'Todos'},
                            ])}
                            valueField="id"
                            selectNotNulleable
                        />
                    </Grid>
                    <Grid item md={5} sm={6} xs={12}>
                        <ControlledAutocomplete
                            loadOptions={HttpDumpTrucks.getStatusTypes}
                            control={control}
                            name={DumpTruckFilterFields.StatusCodeList}
                            label={'Estado'}
                            multiple
                            valueField={'id'}
                            TagComponent={(value: EntityWithIdAndDescription) => {
                                const option: DumpTruckStatusOption = {
                                    [DumpTruckFields.StatusCode]: value[EntityWithIdAndDescriptionFields.Id],
                                    [DumpTruckFields.StatusDescription]: value[EntityWithIdAndDescriptionFields.Description]
                                }

                                return (
                                    (props: any) =>
                                        <DumpTruckStatusChip option={option} {...props}/>
                                )
                            }}
                            renderOption={(renderProps, value: EntityWithIdAndDescription, state) => {
                                const color = getDumpTruckStatusColor(value[EntityWithIdAndDescriptionFields.Id])

                                return (
                                    <MenuItem key={state.index} selected={state.selected}
                                              value={value.id} {...renderProps}>
                                        <ListItemIcon>
                                            <Icon
                                                icon='mdi:circle'
                                                color={color}
                                                fontSize='1rem'
                                            />
                                        </ListItemIcon>
                                        {value.descripcion}
                                    </MenuItem>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item md={3.5} sm={12} xs={12}>
                        <ControlledDateRangePicker
                            control={control}
                            label={'Fecha de Entrega'}
                            startDateName={DumpTruckFilterFields.DeliveryDateStart}
                            endDateName={DumpTruckFilterFields.DeliveryDateEnd}
                        />
                    </Grid>
                    <Grid item md={3.5} sm={12} xs={12}>
                        <ControlledDateRangePicker
                            control={control}
                            label={'Fecha de Retiro'}
                            startDateName={DumpTruckFilterFields.RemovalDateStart}
                            endDateName={DumpTruckFilterFields.RemovalDateEnd}
                        />
                    </Grid>

                    <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button
                            variant='contained'
                            onClick={handleSubmit(onSearch)}
                            startIcon={<SearchIcon/>}
                        >
                            Buscar
                        </Button>
                    </Grid>


                </Grid>


            </TableWithPagingAndFilter>

            {dialogOpen &&
                    <DumpTruckCreateDialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        onAfterSubmit={() => {
                            refetch();
                            setDialogOpen(false);
                        }}
                        maxWidth={'md'}
                        dumpTruck={selectedDumpTruck}
                    />

            }
        </>
    );
}

export default DumpTrucks;
